import React, { useState } from 'react';

const AdminPanel = ({ blogPosts, setBlogPosts, onClose }) => {
  // Helper: strip HTML to plain text and keep basic line breaks
  const toPlainText = (html) => {
    if (!html || typeof html !== 'string') return '';
    try {
      let text = html
        .replace(/<\s*br\s*\/?\s*>/gi, '\n')
        .replace(/<\s*\/(p|li|h[1-6])\s*>/gi, '\n')
        .replace(/<[^>]+>/g, '');
      text = text
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');
      return text;
    } catch {
      return html;
    }
  };
  const [newPost, setNewPost] = useState({
    title: '',
    date: new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }),
    category: '',
    excerpt: '',
    content: '',
    image: null
  });

  const [editingPost, setEditingPost] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPost(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setNewPost(prev => ({
          ...prev,
          image: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!newPost.title || !newPost.category || !newPost.excerpt || !newPost.content) {
      alert('Please fill in all required fields');
      return;
    }

    const postData = {
      ...newPost,
      content: toPlainText(newPost.content),
      id: editingPost ? editingPost.id : Date.now(),
      image: newPost.image || '/api/placeholder/400/300'
    };

    if (editingPost) {
      // Update existing post
      setBlogPosts(prev => prev.map(post => 
        post.id === editingPost.id ? postData : post
      ));
    } else {
      // Add new post
      setBlogPosts(prev => [postData, ...prev]);
    }

    // Reset form
    setNewPost({
      title: '',
      date: new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      category: '',
      excerpt: '',
      content: '',
      image: null
    });
    setEditingPost(null);
    setShowForm(false);
  };

  const handleEdit = (post) => {
    setNewPost({ ...post, content: toPlainText(post.content) });
    setEditingPost(post);
    setShowForm(true);
  };

  const handleDelete = (postId) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      setBlogPosts(prev => prev.filter(post => post.id !== postId));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-6xl w-full mt-6 max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
          <h2 className="text-3xl font-bold text-gray-800">Blog Admin Panel</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-red-600 text-4xl font-bold w-10 h-10 flex items-center justify-center rounded-full hover:bg-red-50 border-2 border-gray-300 hover:border-red-300 bg-white shadow-md"
            type="button"
            title="Close Admin Panel"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <button
              onClick={() => setShowForm(!showForm)}
              className="btn-global"
            >
              {showForm ? 'Cancel' : 'Add New Blog Post'}
            </button>
          </div>

          {showForm && (
            <div className="bg-gray-50 p-6 rounded-lg mb-8">
              <h3 className="text-2xl font-bold mb-4">
                {editingPost ? 'Edit Blog Post' : 'Create New Blog Post'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={newPost.title}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Category *
                    </label>
                    <input
                      type="text"
                      name="category"
                      value={newPost.category}
                      onChange={handleInputChange}
                      placeholder="e.g., Admission, Student"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Date
                  </label>
                  <input
                    type="text"
                    name="date"
                    value={newPost.date}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Excerpt (Short Description) *
                  </label>
                  <textarea
                    name="excerpt"
                    value={newPost.excerpt}
                    onChange={handleInputChange}
                    rows="2"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Content *
                  </label>
                  <textarea
                    name="content"
                    value={newPost.content}
                    onChange={handleInputChange}
                    rows="10"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Upload Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  {newPost.image && (
                    <img
                      src={newPost.image}
                      alt="Preview"
                      className="mt-2 w-32 h-32 object-cover rounded-lg"
                    />
                  )}
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="btn-global"
                  >
                    {editingPost ? 'Update Post' : 'Create Post'}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingPost(null);
                      setNewPost({
                        title: '',
                        date: new Date().toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        }),
                        category: '',
                        excerpt: '',
                        content: '',
                        image: null
                      });
                    }}
                    className="btn-global"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="space-y-4">
            <h3 className="text-2xl font-bold">Existing Blog Posts ({blogPosts.length})</h3>
            
            {blogPosts.map((post) => (
              <div key={post.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="text-xl font-bold text-gray-800 mb-2">{post.title}</h4>
                    <p className="text-green-600 text-sm font-semibold mb-2">
                      {post.date} / {post.category}
                    </p>
                    <p className="text-gray-600 mb-2">{post.excerpt}</p>
                  </div>
                  
                  {post.image && (
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-24 h-24 object-cover rounded-lg ml-4"
                    />
                  )}
                </div>
                
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(post)}
                    className="btn-global"
                  >
                    Edit
                  </button>
                  
                  <button
                    onClick={() => handleDelete(post.id)}
                    className="btn-global"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
